/**
 * auditStyles.js
 * Standalone CSS injection for the Audit module.
 * Same design tokens as kpiStyles — no cross-module import needed.
 */

export const injectAuditStyles = () => {
  if (document.getElementById("audit-system-styles")) return;

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Fira+Code:wght@400;500;600;700&display=swap";
  document.head.appendChild(link);

  const s = document.createElement("style");
  s.id = "audit-system-styles";
  s.textContent = `
    *, *::before, *::after { box-sizing: border-box; }

    :root {
      --ink:       #0d0d0d;
      --ink-2:     #3d3d3d;
      --ink-3:     #6b7280;
      --ink-4:     #9ca3af;
      --rule:      #e5e7eb;
      --rule-2:    #f3f4f6;
      --surface:   #ffffff;
      --surface-2: #f9fafb;
      --surface-3: #f3f4f6;
      --accent:    #2563eb;
      --accent-2:  #dbeafe;
      --accent-3:  #eff6ff;
      --green:     #16a34a;
      --green-2:   #dcfce7;
      --amber:     #d97706;
      --amber-2:   #fef3c7;
      --red:       #dc2626;
      --red-2:     #fee2e2;
      --purple:    #7c3aed;
      --purple-2:  #ede9fe;
      --teal:      #0d9488;
      --teal-2:    #ccfbf1;
      --font-sans: 'Syne', sans-serif;
      --font-mono: 'Fira Code', monospace;
    }

    // .audit-root * { font-family: var(--font-sans); }

    /* ── Inputs ── */
    .audit-input {
      width: 100%;
      padding: 8px 12px;
      border: 1.5px solid var(--rule);
      border-radius: 8px;
      font-size: 13px;
      font-family: var(--font-sans);
      color: var(--ink);
      background: var(--surface);
      outline: none;
      transition: border-color 0.15s, box-shadow 0.15s;
      appearance: auto;
    }
    .audit-input:focus {
      border-color: var(--accent);
      box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
    }
    .audit-input::placeholder { color: var(--ink-4); font-size: 12.5px; }

    .audit-label {
      display: block;
      font-size: 10.5px;
      font-weight: 700;
      color: var(--ink-3);
      text-transform: uppercase;
      letter-spacing: 0.07em;
      font-family: var(--font-sans);
      margin-bottom: 6px;
    }

    /* ── Buttons ── */
    .audit-btn-primary {
      padding: 9px 22px;
      background: var(--ink);
      color: #fff;
      border: none;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 700;
      font-family: var(--font-sans);
      cursor: pointer;
      letter-spacing: 0.02em;
      transition: all 0.15s;
    }
    .audit-btn-primary:hover { background: #1a1a1a; transform: translateY(-1px); }
    .audit-btn-primary:disabled { background: var(--rule); color: var(--ink-4); cursor: not-allowed; transform: none; }

    .audit-btn-secondary {
      padding: 9px 16px;
      background: transparent;
      color: var(--ink-3);
      border: 1.5px solid var(--rule);
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      font-family: var(--font-sans);
      cursor: pointer;
      transition: all 0.15s;
    }
    .audit-btn-secondary:hover { background: var(--surface-2); border-color: #d1d5db; }

    .audit-btn-approve {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 7px 16px; border-radius: 8px;
      font-size: 12px; font-weight: 700; font-family: var(--font-sans);
      cursor: pointer; border: 1.5px solid #bbf7d0;
      background: var(--green-2); color: var(--green);
      transition: all 0.15s;
    }
    .audit-btn-approve:hover { background: #bbf7d0; transform: translateY(-1px); }
    .audit-btn-approve:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

    .audit-btn-reject {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 7px 16px; border-radius: 8px;
      font-size: 12px; font-weight: 700; font-family: var(--font-sans);
      cursor: pointer; border: 1.5px solid #fca5a5;
      background: var(--red-2); color: var(--red);
      transition: all 0.15s;
    }
    .audit-btn-reject:hover { background: #fca5a5; transform: translateY(-1px); }
    .audit-btn-reject:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

    .audit-btn-assign {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 5px 11px; border-radius: 6px;
      font-size: 11.5px; font-weight: 700; font-family: var(--font-sans);
      cursor: pointer; border: 1.5px solid #ddd6fe;
      background: var(--purple-2); color: var(--purple);
      transition: all 0.15s;
    }
    .audit-btn-assign:hover { background: #ddd6fe; }

    /* ── Checkbox ── */
    .audit-checkbox {
      width: 16px; height: 16px;
      border: 2px solid var(--rule); border-radius: 4px;
      cursor: pointer; appearance: none;
      background: var(--surface); transition: all 0.12s;
      flex-shrink: 0; position: relative;
    }
    .audit-checkbox:checked { background: var(--accent); border-color: var(--accent); }
    .audit-checkbox:checked::after {
      content: ''; position: absolute;
      left: 3px; top: 1px; width: 5px; height: 8px;
      border: 2px solid #fff; border-top: none; border-left: none;
      transform: rotate(45deg);
    }

    /* ── Modal ── */
    .audit-modal-overlay {
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.45); backdrop-filter: blur(2px);
      z-index: 1000; display: flex; align-items: center; justify-content: center;
      padding: 20px; animation: auditFadeIn 0.15s ease;
    }
    .audit-modal-box {
      background: var(--surface); border-radius: 16px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.2);
      width: 100%; max-height: 90vh; overflow: hidden;
      display: flex; flex-direction: column;
      animation: auditScaleIn 0.2s ease;
    }

    /* ── Inline select ── */
    .audit-inline-select {
      padding: 7px 11px; border: 1.5px solid var(--rule); border-radius: 8px;
      font-size: 12.5px; font-family: var(--font-sans); font-weight: 600;
      color: var(--ink-2); background: var(--surface); cursor: pointer; outline: none; transition: all 0.15s;
    }
    .audit-inline-select:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
    .audit-inline-select.active { border-color: var(--accent); background: var(--accent-3); color: var(--accent); }

    /* ── Filter pill ── */
    .audit-filter-pill {
      padding: 6px 14px; border-radius: 8px; cursor: pointer;
      font-size: 12px; font-weight: 600; font-family: var(--font-sans);
      transition: all 0.15s; border: 1.5px solid var(--rule);
      background: var(--surface); color: var(--ink-2);
    }
    .audit-filter-pill.active { border-color: var(--accent); background: var(--accent-3); color: var(--accent); }

    /* ── Status badges ── */
    .audit-badge {
      display: inline-flex; align-items: center; gap: 4px;
      font-size: 10px; font-weight: 700; padding: 3px 10px; border-radius: 20px;
      letter-spacing: 0.04em; text-transform: uppercase; font-family: var(--font-mono);
    }
    .audit-badge-pending    { background: var(--amber-2);  color: var(--amber);  border: 1.5px solid #fde68a; }
    .audit-badge-approved   { background: var(--green-2);  color: var(--green);  border: 1.5px solid #bbf7d0; }
    .audit-badge-rejected   { background: var(--red-2);    color: var(--red);    border: 1.5px solid #fca5a5; }
    .audit-badge-in-review  { background: var(--accent-3); color: var(--accent); border: 1.5px solid #bfdbfe; }

    /* ── Level badge ── */
    .audit-level-badge {
      display: inline-flex; align-items: center; justify-content: center;
      width: 22px; height: 22px; border-radius: "50%";
      font-size: 10px; font-weight: 800; font-family: var(--font-mono);
      background: var(--surface-3); color: var(--ink-3); border: 1.5px solid var(--rule);
    }

    /* ── Location group header ── */
    .audit-location-header {
      display: flex; align-items: center; gap: 12;
      padding: 10px 20px; background: linear-gradient(90deg, #f0f4ff, #f8fafc);
      border-bottom: 1.5px solid var(--accent-2);
      position: sticky; top: 42px; z-index: 8;
    }

    /* ── Shimmer ── */
    .audit-shimmer {
      background: linear-gradient(90deg, #f1f5f9 25%, #e8edf5 50%, #f1f5f9 75%);
      background-size: 200% 100%; animation: auditShimmer 1.4s infinite;
    }

    /* ── Spinner ── */
    .audit-spinner {
      width: 24px; height: 24px;
      border: 2.5px solid var(--rule); border-top-color: var(--accent);
      border-radius: 50%; animation: auditSpin 0.7s linear infinite; flex-shrink: 0;
    }

    /* ── Bulk bar ── */
    .audit-bulk-bar { animation: auditSlideDown 0.2s ease; }

    /* ── Dropdown ── */
    .audit-dropdown-menu {
      position: absolute; top: calc(100% + 4px); right: 0;
      background: var(--surface); border: 1.5px solid var(--rule); border-radius: 10px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.12); z-index: 200; min-width: 200px;
      overflow: hidden; animation: auditSlideDown 0.15s ease;
    }
    .audit-dropdown-item {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 14px; font-size: 13px; cursor: pointer;
      transition: background 0.1s; color: var(--ink-2); font-weight: 500;
    }
    .audit-dropdown-item:hover { background: var(--surface-2); }

    /* ── Tab ── */
    .audit-tab {
      padding: 8px 18px; border: none; background: transparent; cursor: pointer;
      font-size: 12.5px; font-weight: 600; font-family: var(--font-sans);
      color: var(--ink-3); border-bottom: 2px solid transparent;
      transition: all 0.15s; white-space: nowrap;
    }
    .audit-tab.active { color: var(--accent); border-bottom-color: var(--accent); font-weight: 700; }

    /* ── Search highlight ── */
    .audit-search-mark { background: #fef08a; border-radius: 2px; padding: 0 1px; }
    

    /* ── Animations ── */
    @keyframes auditFadeIn    { from { opacity: 0; }                              to { opacity: 1; } }
    @keyframes auditScaleIn   { from { opacity: 0; transform: scale(0.96); }     to { opacity: 1; transform: scale(1); } }
    @keyframes auditSlideDown { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes auditSpin      { to { transform: rotate(360deg); } }
    @keyframes auditShimmer   { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
    .audit-slide-down { animation: auditSlideDown 0.22s ease; }
  `;
  document.head.appendChild(s);
};
